import React, { useEffect, useState } from 'react';
import { Card, Typography, Row, Col, DatePicker, List, Spin, Button } from 'antd';
import { LoadingOutlined, DownloadOutlined, FileTextOutlined } from '@ant-design/icons';
import dayjs, { Dayjs } from 'dayjs';
import { useDispatch, useSelector } from 'react-redux';
import { isEmpty } from 'underscore';

import { AppDispatch, CombinedStates } from '../../core/types';
import { getSchoolIndicatorCategories, getEducationScopeTimeSeries, exportEducationScope, exportEducationScopePdf } from '../../core/indicator/indicatorApi';
import { Category, TimeSeriesIndicator } from '../../core/indicator/types';
import SingleLineChart from './SingleLineChart';

import './EducationCenterIndicators.scss';

const { Title, Paragraph, Text } = Typography;
const { RangePicker } = DatePicker;

interface EducationCenterIndicatorsProps {
    assigned_scope: 'education_center' | 'education_department';
}

const EducationCenterIndicators: React.FC<EducationCenterIndicatorsProps> = ({ assigned_scope }) => {
    const [dateRange, setDateRange] = useState<[Dayjs, Dayjs] | null>(null);
    const [selectedCategory, setSelectedCategory] = useState<number | null>(null);
    const dispatch = useDispatch<AppDispatch>();
    const [categories, setCategories] = useState<Category[]>([]);

    const {
        educationCenterIndicatorCategories,
        educationDepartmentIndicatorCategories,
        educationScopeTimeSeries,
        isFetchingEducationScopeTimeSeries,
        isFetching,
        isFetchingEducationScopeExport,
        isFetchingEducationScopePdfExport,
    } = useSelector((state: CombinedStates) => state.indicator);

    useEffect(() => {
        if (assigned_scope === 'education_center') {
            setCategories(educationCenterIndicatorCategories);
        } else if (assigned_scope === 'education_department') {
            setCategories(educationDepartmentIndicatorCategories);
        }
    }, [assigned_scope, educationCenterIndicatorCategories, educationDepartmentIndicatorCategories]);

    useEffect(() => {
        if (isEmpty(categories)) {
            dispatch(getSchoolIndicatorCategories({ assigned_scope }));
        }
    }, [dispatch, assigned_scope]);

    // Get current school year (September 1st to August 31st)
    const getCurrentSchoolYear = (): [Dayjs, Dayjs] => {
        const now = dayjs();
        const currentYear = now.year();

        // If current month is before September, school year started last year
        const schoolYearStart = now.month() < 8 ? currentYear - 1 : currentYear;
        const schoolYearEnd = schoolYearStart + 1;

        return [
            dayjs(`${schoolYearStart}-09-01`), // September 1st
            dayjs(`${schoolYearEnd}-08-31`), // August 31st
        ];
    };

    useEffect(() => {
        // Set default date range to current school year
        setDateRange(getCurrentSchoolYear());
    }, []);

    // Effect to handle API calls when both date range and category are available
    useEffect(() => {
        if (dateRange && selectedCategory) {
            console.log('Both date range and category are set, calling API:', {
                categoryId: selectedCategory,
                from: dayjs(dateRange[0]).format('YYYY-MM-DD'),
                to: dayjs(dateRange[1]).format('YYYY-MM-DD'),
            });

            dispatch(getEducationScopeTimeSeries({
                scope: assigned_scope,
                category_id: selectedCategory,
                date_from: dayjs(dateRange[0]).format('YYYY-MM-DD'),
                date_to: dayjs(dateRange[1]).format('YYYY-MM-DD'),
            }));
        }
    }, [dateRange, selectedCategory, dispatch, assigned_scope]);

    const handleDateRangeChange = (dates: [Dayjs | null, Dayjs | null] | null) => {
        if (dates && dates[0] && dates[1]) {
            setDateRange([dates[0], dates[1]]);
            console.log('Date range changed:', {
                from: dayjs(dates[0]).format('YYYY-MM-DD'),
                to: dayjs(dates[1]).format('YYYY-MM-DD'),
            });
            // The useEffect will handle the API call when both dateRange and selectedCategory are set
        } else {
            setDateRange(null);
        }
    };

    const handleCategorySelect = (categoryId: number) => {
        setSelectedCategory(categoryId);
        console.log('Selected category ID:', categoryId);
        // The useEffect will handle the API call when both dateRange and selectedCategory are set
    };

    const handleExport = async () => {
        if (!dateRange || !selectedCategory) {
            return;
        }

        try {
            const response = await dispatch(exportEducationScope({
                scope: assigned_scope,
                category_id: selectedCategory,
                date_from: dayjs(dateRange[0]).format('YYYY-MM-DD'),
                date_to: dayjs(dateRange[1]).format('YYYY-MM-DD'),
                format: 'xlsx',
            })).unwrap();

            // Create blob and download
            const blob = new Blob([response.data], {
                type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
            });
            const url = window.URL.createObjectURL(blob);
            const link = document.createElement('a');
            link.href = url;
            link.download = `svietimo_rodikliai_${assigned_scope}_${new Date().toISOString().split('T')[0]}.xlsx`;
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);
            window.URL.revokeObjectURL(url);
        } catch (error) {
            console.error('Export failed:', error);
        }
    };

    const handleExportPdf = async () => {
        if (!dateRange || !selectedCategory) {
            return;
        }

        try {
            const response = await dispatch(exportEducationScopePdf({
                scope: assigned_scope,
                category_id: selectedCategory,
                date_from: dayjs(dateRange[0]).format('YYYY-MM-DD'),
                date_to: dayjs(dateRange[1]).format('YYYY-MM-DD'),
            })).unwrap();

            // Create blob and download
            const blob = new Blob([response.data], {
                type: 'application/pdf',
            });
            const url = window.URL.createObjectURL(blob);
            const link = document.createElement('a');
            link.href = url;
            link.download = `svietimo_rodikliai_${assigned_scope}_${new Date().toISOString().split('T')[0]}.pdf`;
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);
            window.URL.revokeObjectURL(url);
        } catch (error) {
            console.error('PDF export failed:', error);
        }
    };

    const getScopeTitle = () => (assigned_scope === 'education_center'
        ? 'Švietimo Centro Rodikliai'
        : 'Švietimo Skyriaus Rodikliai');

    const getScopeDescription = () => (assigned_scope === 'education_center'
        ? 'Čia bus rodoma švietimo centro rodiklių statistika.'
        : 'Čia bus rodoma švietimo skyriaus rodiklių statistika.');

    // Helper function to format data for single line chart
    const formatDataForSingleLineChart = (indicator: TimeSeriesIndicator) => {
        const chartData: Array<{
            period: string;
            periodDescription: string;
            value: number;
            displayValue: string;
            confirmed: boolean;
            updatedAt: string;
        }> = [];

        indicator.time_series.forEach((dataPoint) => {
            chartData.push({
                period: dataPoint.period,
                periodDescription: dataPoint.period_description,
                value: parseFloat(dataPoint.value.toString()),
                displayValue: dataPoint.value_text || dataPoint.value.toString(),
                confirmed: dataPoint.confirmed_at !== null,
                updatedAt: dataPoint.updated_at,
            });
        });

        return chartData;
    };

    // Check if indicator should show line chart (skip for taip_ne and text units)
    const shouldShowLineChart = (unit: string) => unit !== 'taip_ne' && unit !== 'text';

    return (
        <div className="education-center-indicators">
            {/* Date Range Picker */}
            <div className="date-range-section" style={{ marginBottom: 24 }}>
                <Row gutter={16} align="middle">
                    <Col xs={24} sm={8}>
                        <Text strong>Laikotarpis:</Text>
                    </Col>
                    <Col xs={24} sm={16}>
                        <RangePicker
                            value={dateRange}
                            onChange={handleDateRangeChange}
                            format="YYYY-MM-DD"
                            placeholder={['Pradžios data', 'Pabaigos data']}
                            className="date-range-picker"
                            allowClear={false} />
                    </Col>
                </Row>
            </div>

            <Row gutter={24}>
                {/* Left side - Categories */}
                <Col xs={24} md={6} lg={6}>
                    <div className="categories-section">
                        <Title level={4}>Rodiklių kategorijos</Title>
                        {isFetching && (
                            <div className="loading-container">
                                <Spin />
                            </div>
                        )}
                        <List
                            className="categories-list"
                            dataSource={categories}
                            renderItem={(category) => (
                                <List.Item
                                    className={`category-item ${selectedCategory === category.id ? 'selected' : ''}`}
                                    onClick={() => handleCategorySelect(category.id)}>
                                    <div className="category-content">
                                        <Title level={5} className="category-title">
                                            {category.title}
                                        </Title>
                                        {category.description && (
                                            <Text type="secondary" className="category-description">
                                                {category.description}
                                            </Text>
                                        )}
                                    </div>
                                </List.Item>
                            )} />
                    </div>
                </Col>

                {/* Right side - Content */}
                <Col xs={24} md={18} lg={18}>
                    <Card>
                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
                            <Title level={3} style={{ margin: 0 }}>{getScopeTitle()}</Title>
                            <div style={{ display: 'flex', gap: '12px' }}>
                                <Button
                                    type="default"
                                    icon={<DownloadOutlined />}
                                    onClick={handleExport}
                                    loading={isFetchingEducationScopeExport}
                                    disabled={!dateRange || !selectedCategory}
                                    style={{
                                        backgroundColor: '#ffffff',
                                        borderColor: '#d9d9d9',
                                        color: '#262626',
                                        borderRadius: '6px',
                                        height: '40px',
                                        paddingLeft: '20px',
                                        paddingRight: '20px',
                                        fontSize: '14px',
                                        fontWeight: '500',
                                        boxShadow: '0 2px 4px rgba(0, 0, 0, 0.1)',
                                        border: '1px solid #d9d9d9',
                                    }}>
                                    Eksportuoti į Excel
                                </Button>
                                <Button
                                    type="default"
                                    icon={<FileTextOutlined />}
                                    onClick={handleExportPdf}
                                    loading={isFetchingEducationScopePdfExport}
                                    disabled={!dateRange || !selectedCategory}
                                    style={{
                                        backgroundColor: '#ffffff',
                                        borderColor: '#d9d9d9',
                                        color: '#262626',
                                        borderRadius: '6px',
                                        height: '40px',
                                        paddingLeft: '20px',
                                        paddingRight: '20px',
                                        fontSize: '14px',
                                        fontWeight: '500',
                                        boxShadow: '0 2px 4px rgba(0, 0, 0, 0.1)',
                                        border: '1px solid #d9d9d9',
                                    }}>
                                    Eksportuoti į PDF
                                </Button>
                            </div>
                        </div>
                        <Paragraph>
                            {getScopeDescription()}
                        </Paragraph>
                        {dateRange && (
                            <Paragraph type="secondary">
                                Pasirinktas laikotarpis: {dayjs(dateRange[0]).format('YYYY-MM-DD')} - {dayjs(dateRange[1]).format('YYYY-MM-DD')}
                            </Paragraph>
                        )}

                        {isFetchingEducationScopeTimeSeries && (
                            <div className="loading-container">
                                <Spin
                                    indicator={(
                                        <LoadingOutlined
                                            style={{
                                                fontSize: 48,
                                                color: '#fdab01',
                                                animation: 'spin 1s linear infinite',
                                            }} />
                                    )} />
                                <div className="loading-text-primary">
                                    Kraunama duomenys...
                                </div>
                                <div className="loading-text-secondary">
                                    Prašome palaukti
                                </div>
                            </div>
                        )}

                        {!isFetchingEducationScopeTimeSeries && educationScopeTimeSeries && educationScopeTimeSeries.success && (
                            <div style={{ marginTop: 16 }}>
                                <Title level={4}>Rodiklių duomenys</Title>
                                <Paragraph type="secondary">
                                    Sritis: <strong>{educationScopeTimeSeries.time_series.scope_name}</strong>
                                </Paragraph>
                                <Paragraph type="secondary">
                                    Rodiklių skaičius: <strong>{educationScopeTimeSeries.time_series.statistics.total_indicators}</strong>
                                </Paragraph>
                                <Paragraph type="secondary">
                                    Duomenų užpildymas: <strong>{educationScopeTimeSeries.time_series.statistics.data_completion_rate}%</strong>
                                </Paragraph>

                                {/* Detailed Time Series Charts */}
                                <div style={{ marginTop: 24 }}>
                                    <Title level={5}>Detalūs laiko eilutės grafikai</Title>
                                    <div>
                                        {educationScopeTimeSeries.time_series.indicators.map((indicator) => {
                                            const chartData = formatDataForSingleLineChart(indicator);

                                            if (chartData.length === 0) {
                                                return (
                                                    <div key={indicator.id} style={{ marginBottom: 32 }}>
                                                        <Card size="small" style={{ marginTop: 8 }}>
                                                            <Title level={5}>{indicator.title}</Title>
                                                            <div style={{ textAlign: 'center', padding: '40px 20px' }}>
                                                                <Text type="secondary">Nėra duomenų šiam rodikliui</Text>
                                                            </div>
                                                        </Card>
                                                    </div>
                                                );
                                            }

                                            if (!shouldShowLineChart(indicator.unit)) {
                                                return (
                                                    <div key={indicator.id} style={{ marginBottom: 32 }}>
                                                        <Card size="small" style={{ marginTop: 8 }}>
                                                            <Title level={5}>{indicator.title}</Title>
                                                            <Paragraph type="secondary">
                                                                Vienetas: {indicator.unit_description}
                                                            </Paragraph>
                                                            <div style={{ marginTop: 16 }}>
                                                                {indicator.time_series.map((dataPoint) => (
                                                                    <div key={dataPoint.period} style={{ marginBottom: 8, padding: '8px', backgroundColor: '#f5f5f5', borderRadius: '4px' }}>
                                                                        <Text strong>{dataPoint.period_description}:</Text>
                                                                        <Text style={{ marginLeft: 8 }}>{dataPoint.value}</Text>
                                                                        {dataPoint.confirmed_at && (
                                                                            <Text type="secondary" style={{ marginLeft: 8 }}>
                                                                                (patvirtinta)
                                                                            </Text>
                                                                        )}
                                                                    </div>
                                                                ))}
                                                            </div>
                                                        </Card>
                                                    </div>
                                                );
                                            }

                                            return (
                                                <div key={indicator.id} style={{ marginBottom: 32 }}>
                                                    <Card size="small" style={{ marginTop: 8 }}>
                                                        <Title level={5}>{indicator.title}</Title>
                                                        <Paragraph type="secondary">
                                                            Vienetas: {indicator.unit_description}
                                                        </Paragraph>
                                                        <Paragraph type="secondary">
                                                            Vidurkis: <strong>{indicator.statistics.average}</strong> |
                                                            Min: <strong>{indicator.statistics.min}</strong> |
                                                            Max: <strong>{indicator.statistics.max}</strong>
                                                        </Paragraph>

                                                        {/* Chart */}
                                                        <div style={{ marginTop: 16 }}>
                                                            <SingleLineChart
                                                                data={chartData}
                                                                height={300} />
                                                        </div>

                                                        {/* Data Table */}
                                                        <div style={{ marginTop: 16, paddingTop: 16, borderTop: '1px solid #f0f0f0' }}>
                                                            <Title level={5} style={{ marginBottom: 12 }}>Duomenų lentelė</Title>
                                                            <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px' }}>
                                                                {indicator.time_series.map((dataPoint) => (
                                                                    <div
                                                                        key={dataPoint.period}
                                                                        style={{
                                                                            padding: '8px 12px',
                                                                            backgroundColor: '#f0f0f0',
                                                                            borderRadius: '4px',
                                                                            minWidth: '120px',
                                                                        }}>
                                                                        <div style={{ fontSize: '12px', color: '#666' }}>
                                                                            {dataPoint.period_description}
                                                                        </div>
                                                                        <div style={{ fontSize: '14px', fontWeight: 'bold' }}>
                                                                            {dataPoint.value}
                                                                        </div>
                                                                        {dataPoint.confirmed_at && (
                                                                            <div style={{ fontSize: '10px', color: '#52c41a' }}>
                                                                                ✓ Patvirtinta
                                                                            </div>
                                                                        )}
                                                                    </div>
                                                                ))}
                                                            </div>
                                                        </div>
                                                    </Card>
                                                </div>
                                            );
                                        })}
                                    </div>
                                </div>
                            </div>
                        )}
                    </Card>
                </Col>
            </Row>
        </div>
    );
};

export default EducationCenterIndicators;
